import { useRouter } from 'expo-router';
import { useSetAtom } from 'jotai';
import { Link2 } from 'lucide-react-native';
import { memo, useCallback, useMemo } from 'react';

import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useConversation } from '@/hooks/useConversation';
import { referenceMessageBodyAtom } from '@/services/atoms/inquiry';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import { segmentClient } from '@/util/analytics';
import { getStackedInquiryHref } from '@/util/stackedInquiryHref';

type LinkToInquiryActionProps = {
  conversationSid: string;
  inquiry: InquiryV2;
  isSending: boolean;
};

/**
 * Button to create a new inquiry linked to the current conversation (reference).
 *
 * Shown only to Primum users in the chat toolbar. Navigates to the inquiry form
 * with pre-filled message and cancer type from the current inquiry.
 * Reference text is the inquiry question (the first message). Uses the oldest message
 * in the cache (lowest Twilio index) to avoid author-matching issues when Twilio
 * identity format differs from numeric userId.
 *
 * The greeting shown to the target expert(s) is not set here: `NewInquiry.tsx` passes
 * `isReferenceInquiry` to `MessageField`, which builds it per-expert via `expertNotes`
 * once an expert is selected on the form.
 */
export const LinkToInquiryAction = memo(function LinkToInquiryAction({
  conversationSid,
  inquiry,
  isSending,
}: LinkToInquiryActionProps) {
  const router = useRouter();

  const setReferenceMessageBody = useSetAtom(referenceMessageBodyAtom);

  const messages = useConversation(conversationSid);

  const referenceMessageBody = useMemo(() => {
    if (messages.length === 0) {
      return '';
    }

    const oldest = messages.reduce((min, m) => {
      const idx = typeof m._id === 'number' ? m._id : Number(m._id);

      const minIdx = typeof min._id === 'number' ? min._id : Number(min._id);

      return Number.isFinite(idx) && idx < minIdx ? m : min;
    }, messages[0]);

    return ((oldest?.text as string) ?? '').trim();
  }, [messages]);

  const handlePress = useCallback(() => {
    segmentClient.track(ANALYTICS_EVENTS.LINK_TO_INQUIRY_TRIGGERED, {
      conversationSid,
      cancerTypeId: inquiry?.cancerTypeId,
    });

    if (referenceMessageBody) {
      setReferenceMessageBody(referenceMessageBody);
    }

    const params: Record<string, string> = {
      referenceConversationId: conversationSid,
    };

    const cancerTypeId = inquiry?.cancerTypeId;

    if (typeof cancerTypeId === 'number' && Number.isFinite(cancerTypeId)) {
      params.referenceCancerFocusId = String(cancerTypeId);
    }

    router.push(getStackedInquiryHref(params));
  }, [
    conversationSid,
    referenceMessageBody,
    inquiry?.cancerTypeId,
    router,
    setReferenceMessageBody,
  ]);

  const isDisabled = isSending;

  return (
    <Pressable
      disabled={isDisabled}
      onPress={handlePress}
      hitSlop={ICON_BUTTON_HIT_SLOP}
      className="h-10 w-10 items-center justify-center rounded-full active:opacity-70"
      {...getAccessibilityProps({
        label: 'Link to inquiry',
        role: 'button',
        hint: isDisabled
          ? 'A message is being sent'
          : 'Opens inquiry form linked to this conversation',
      })}
      accessibilityState={{ disabled: isDisabled }}>
      <Icon
        as={Link2}
        size="lg"
        className={isDisabled ? 'text-typography-300' : 'text-primary-500'}
      />
    </Pressable>
  );
});
